import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const jobOpenings = [
  {
    id: 1,
    title: 'Application Developer',
    jobId: 'ONEIT-2025-01',
    fields: [
      {
        label: 'Job Title',
        text: 'Application Developer'
      },
      {
        label: 'Job Duties',
        duties: [
          'Collaborate on design and reviews to ensure timely delivery of business applications. 20%',
          'Conduct GAP analysis to identify requirement discrepancies, document complexity, and define implementation strategies, include viable workarounds. 20%',
          'Develop applications and create detailed functional specification documents, include business requirements, development approach, pseudo-logic, and testing criteria. 20%',
          'Execute unit and functional testing to validate implemented features, ensure usability and maintainability for end users. 10%',
          'Design and run Unit, Integration, and UAT test scripts to validate new functionality against user requirements and ensure comprehensive documentation. 10%',
          'Participate across multiple SDLC phases, including requirement gathering, analysis, design, and development. 10%',
          'Prepare essential documentation such as design specifications, test cases, and process workflows. 10%',
        ]
      },
      {
        label: 'Position Requirements',
        text: "Bachelor's degree in Computer Science, Information Systems, Software Engineering, Electronics and Communication Engineering, or related field."
      },
      {
        label: 'Special Requirements',
        text: "Employer will accept any suitable combination of education, training, or experience determined to be equivalent to a U.S. bachelor's degree by an accredited credentials evaluation service."
      },
      {
        label: 'Position requires travel/relocation',
        text: 'Various unanticipated client locations throughout the U.S. with expenses paid by the employer.'
      },
      {
        label: 'Employment Type',
        text: 'Full-Time, Permanent'
      },
      {
        label: 'Location',
        text: '4100 Alpha Rd, Suite 478, Dallas, Texas - 75244'
      },
      {
        label: 'Contact',
        text: 'If interested please mail resume and Job ID to: hr@oneitsolutionsusa.com | Attn: HR Department – OneIT Solutions LLC, 4100 Alpha Rd, Suite 478, Dallas, Texas - 75244'
      },
    ]
  },
  {
    id: 2,
    title: 'Software Developer',
    jobId: 'ONEIT-2025-02',
    fields: [
      {
        label: 'Job Title',
        text: 'Software Developer'
      },
      {
        label: 'Job Duties',
        duties: [
          'Analyze business and technical requirements to design scalable, maintainable software solutions aligned with organizational goals. 20%',
          'Develop, enhance, and maintain software modules across front-end and back-end systems using modern programming languages and frameworks. 20%',
          'Collaborate with architects and senior engineers to define system architecture, data models, and integration patterns. 20%',
          'Implement automated unit, regression, and performance tests to ensure software reliability and code quality standards. 10%',
          'Identify, troubleshoot, and resolve software defects and production issues with minimal disruption to business operations. 10%',
          'Participate in code reviews, technical discussions, and knowledge-sharing sessions to promote engineering best practices. 10%',
          'Maintain up-to-date technical documentation including API references, system diagrams, and deployment runbooks. 10%',
        ]
      },
      {
        label: 'Position Requirements',
        text: "Bachelor's degree in Computer Science, Information Technology, Electronics and Communication Engineering, Engineering (any), or related field."
      },
      {
        label: 'Special Requirements',
        text: "Employer will accept any suitable combination of education, training, or experience determined to be equivalent to a U.S. bachelor's degree by an accredited credentials evaluation service."
      },
      {
        label: 'Position requires travel/relocation',
        text: 'Various unanticipated client locations throughout the U.S. with expenses paid by the employer.'
      },
      {
        label: 'Employment Type',
        text: 'Full-Time, Permanent'
      },
      {
        label: 'Location',
        text: '4100 Alpha Rd, Suite 478, Dallas, Texas - 75244'
      },
      {
        label: 'Contact',
        text: 'If interested please mail resume and Job ID to: hr@oneitsolutionsusa.com | Attn: HR Department – OneIT Solutions LLC, 4100 Alpha Rd, Suite 478, Dallas, Texas - 75244'
      },
    ]
  },
];

const Careers = () => {
  const [expandedJob, setExpandedJob] = useState(null);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Join Our <span className="text-[#1A3E6E]">Team</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Be part of a dynamic team building the future of AI and software solutions.
              We're looking for talented individuals who are passionate about technology and innovation.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="text-4xl mb-3">🚀</div>
                <h3 className="font-bold text-lg mb-2">Innovation First</h3>
                <p className="text-gray-600 text-sm">Work with cutting-edge AI and modern tech stacks</p>
              </CardContent>
            </Card>
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="text-4xl mb-3">🌟</div>
                <h3 className="font-bold text-lg mb-2">Growth Mindset</h3>
                <p className="text-gray-600 text-sm">Continuous learning and professional development</p>
              </CardContent>
            </Card>
            <Card className="text-center hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="text-4xl mb-3">🤝</div>
                <h3 className="font-bold text-lg mb-2">Collaborative Culture</h3>
                <p className="text-gray-600 text-sm">Work with talented teams on impactful projects</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Job Openings */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Open Positions</h2>
            <p className="text-xl text-gray-600">Explore our current openings and find your next opportunity</p>
          </div>

          <div className="space-y-4">
            {jobOpenings.map((job) => (
              <Card key={job.id} className="border border-gray-300 hover:border-[#1A3E6E] transition-all duration-300 shadow-sm">
                {/* Job Header */}
                <div
                  className="flex items-center justify-between px-6 py-4 cursor-pointer"
                  onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                >
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">{job.title}</h3>
                    <p className="text-sm text-gray-500 mt-0.5">Job ID: {job.jobId}</p>
                  </div>
                  <button className="text-[#1A3E6E]">
                    {expandedJob === job.id ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                  </button>
                </div>

                {/* Expanded Content */}
                {expandedJob === job.id && (
                  <CardContent className="border-t border-gray-200 px-6 py-6 space-y-4">
                    {job.fields.map((field, idx) => (
                      <div key={idx} className="text-gray-700 text-sm leading-relaxed">
                        <strong className="text-gray-900">{field.label}:</strong>{' '}
                        {field.duties ? (
                          <ul className="mt-2 space-y-1">
                            {field.duties.map((duty, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span className="text-[#1A3E6E] flex-shrink-0 mt-0.5">·</span>
                                <span>{duty}</span>
                              </li>
                            ))}
                          </ul>
                        ) : field.label === 'Contact' ? (
                          <>
                            If interested please mail resume and Job ID to:{' '}
                            <a href="mailto:hr@oneitsolutionsusa.com" className="text-[#1A3E6E] hover:underline font-medium">
                              hr@oneitsolutionsusa.com
                            </a>
                            {' '}| Attn: HR Department – OneIT Solutions LLC, 4100 Alpha Rd, Suite 478, Dallas, Texas - 75244
                          </>
                        ) : field.text}
                      </div>
                    ))}

                    <div className="pt-4">
                      <Button
                        onClick={() => window.location.href = `mailto:hr@oneitsolutionsusa.com?subject=${job.title} Application - ${job.jobId}`}
                        className="bg-[#1A3E6E] hover:bg-[#1E40AF] text-white px-6 py-2 rounded-md transition-colors duration-200"
                      >
                        Apply Now
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Don't See Your Role?</h2>
          <p className="text-xl text-gray-600 mb-4">
            We're always looking for talented individuals. Send us your resume and we'll keep you in mind for future opportunities.
          </p>
          <p className="text-[#1A3E6E] font-semibold mb-8">hr@oneitsolutionsusa.com</p>
          <Button
            onClick={() => window.location.href = 'mailto:hr@oneitsolutionsusa.com?subject=General Application'}
            className="bg-[#1A3E6E] hover:bg-[#1E40AF] text-white px-8 py-4 text-lg rounded-md transition-all duration-200"
          >
            Submit General Application
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Careers;
